import React, { useEffect, useState } from 'react';
import { startOfDay } from 'date-fns';
import { FormControlLabel, Switch } from '@mui/material';

import CustomAutoExpandingTextarea from '../../../components/Inputs/AutoExpandingTextarea/AutoExpandingTextarea';
import CustomDateRangePicker from '../../../components/Inputs/DateCalendarRange/DateCalendarRange';
import ActionButton from '../../../components/ActionButton/ActionButton';
import { useSurveyRedCertStore } from '../../../../stores/surveyRedCertStore';
import { SurveyFormProps } from './types';
import {
  ButtonContainer,
  ContentRow,
  CreateSurveyContainer,
  DateRangeContainer,
  FormWrapper,
  SectionCard,
  SectionTitle,
} from './styles';
import SurveyQuestions from '../SurveyQuestion/SurveyQuestion';
import { SurveyType } from '../../../../services/cropConnectApiClient';
import EditIcon from '../../../../assets/icons/EditIcon';
import SaveIcon from '../../../../assets/icons/SaveIcon';
import { TranslationKeys } from '../../../../i18n/locales/defaultResources';
import { useI18n } from '../../../../hooks/useI18n';

const SurveyForm = ({
  mode,
  surveyId,
  onCancel,
  onSurveyCreated,
}: SurveyFormProps) => {
  const {
    createSurvey,
    editSurvey,
    fetchSurvey,
    loading,
    setCurrentSurveyAccessId,
    deleteSurveyQuestion,
  } = useSurveyRedCertStore();

  const [surveyName, setSurveyName] = useState('');
  const [surveyDescription, setSurveyDescription] = useState('');
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    startOfDay(new Date()),
    null,
  ]);
  const [activeSurveyId, setActiveSurveyId] = useState<string | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isEditingDates, setIsEditingDates] = useState(false);
  const [originalTitle, setOriginalTitle] = useState('');
  const [originalDescription, setOriginalDescription] = useState('');
  const [originalDateRange, setOriginalDateRange] = useState<
    [Date | null, Date | null]
  >([null, null]);
  const [hasQuestions, setHasQuestions] = useState(false);
  const [hasUnsavedQuestions, setHasUnsavedQuestions] = useState(false);
  const [surveyHint, setSurveyHint] = useState({ text: '', enabled: false });
  const [isEditingHint, setIsEditingHint] = useState(false);
  const [originalHint, setOriginalHint] = useState({
    text: '',
    enabled: false,
  });
  const [surveyConsents, setSurveyConsents] = useState({
    mainConsent: '',
    extraConsent: '',
  });
  const [originalConsents, setOriginalConsents] = useState({
    mainConsent: '',
    extraConsent: '',
  });
  const [surveyHintForInvekos, setSurveyHintForInvekos] = useState('');
  const [originalHintForInvekos, setOriginalHintForInvekos] = useState('');
  const [isEditingHintForInvekos, setIsEditingHintForInvekos] = useState(false);
  const [isEditingConsents, setIsEditingConsents] = useState(false);

  const { t } = useI18n();

  const handleEditHint = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsEditingHint(true);
    setOriginalHint(surveyHint);
  };

  const isFormValid = () => {
    const isNameValid = surveyName.trim().length > 0;
    const isDateValid = !!dateRange[0] && !!dateRange[1];
    return isNameValid && isDateValid;
  };

  useEffect(() => {
    const loadSurveyData = async () => {
      if (mode === 'edit' && surveyId) {
        try {
          const response = await fetchSurvey(surveyId);

          if (response?.data) {
            setSurveyName(response.data.name);
            const additionalProps = response.data.additionalProperties || {};

            setSurveyDescription(additionalProps.description || '');
            setSurveyHint(additionalProps.hint || { text: '', enabled: false });
            setSurveyConsents(
              additionalProps.consents || {
                mainConsent: '',
                extraConsent: '',
              }
            );
            setSurveyHintForInvekos(additionalProps.hintForInvekos || '');
            setDateRange([
              new Date(response.data.startDate),
              new Date(response.data.endDate),
            ]);
            setActiveSurveyId(surveyId);
            setCurrentSurveyAccessId(surveyId);
          }
        } catch (error) {
          console.error('Error loading survey:', error);
        }
      }
    };

    loadSurveyData();
  }, [mode, surveyId, fetchSurvey, setCurrentSurveyAccessId]);

  const handleSaveSurvey = async () => {
    if (!dateRange[0] || !dateRange[1]) return;

    const startDateTime = dateRange[0];
    const endDateTime = dateRange[1];

    const surveyData = {
      name: surveyName,
      headline: surveyName,
      description: surveyDescription,
      startDate: startDateTime?.toISOString() || dateRange[0].toISOString(),
      endDate: endDateTime?.toISOString() || dateRange[1].toISOString(),
      active: false,
      type: 'RedCert' as SurveyType,
      additionalProperties: {
        description: surveyDescription,
        hint: surveyHint,
        consents: surveyConsents,
        hintForInvekos: surveyHintForInvekos,
      },
    };

    try {
      const response = await createSurvey(surveyData);
      if (response?.data) {
        setActiveSurveyId(response.data.accessId);
        setCurrentSurveyAccessId(response.data.accessId);
      }
    } catch (error) {
      console.error('Error saving survey:', error);
    }
  };

  const handleEditConsents = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsEditingConsents(true);
    setOriginalConsents(surveyConsents);
  };

  const handleEditTitle = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsEditingTitle(true);
    setOriginalTitle(surveyName);
  };

  const handleEditDescription = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsEditingDescription(true);
    setOriginalDescription(surveyDescription);
  };

  const handleEditDates = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsEditingDates(true);
    setOriginalDateRange(dateRange);
  };

  const handleSaveTitle = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (activeSurveyId && surveyName !== originalTitle) {
      const currentSurvey = await fetchSurvey(activeSurveyId);

      if (currentSurvey?.data) {
        const updateData = {
          accessId: activeSurveyId,
          name: surveyName,
          headline: surveyName,
          questions: currentSurvey.data.questions || [],
          startDate: currentSurvey.data.startDate,
          endDate: currentSurvey.data.endDate,
          active: currentSurvey.data.active,
          type: 'RedCert' as SurveyType,
          additionalProperties: currentSurvey.data.additionalProperties,
        };

        await editSurvey(activeSurveyId, updateData);
        setIsEditingTitle(false);
      }
    }
  };

  const handleSaveDescription = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (activeSurveyId && surveyDescription !== originalDescription) {
      const currentSurvey = await fetchSurvey(activeSurveyId);
      const updateData = {
        ...currentSurvey?.data,
        additionalProperties: {
          description: surveyDescription,
          hint: surveyHint,
          hintForInvekos: surveyHintForInvekos,
          consents: surveyConsents,
        },
      };

      await editSurvey(activeSurveyId, updateData);
      setIsEditingDescription(false);
    }
  };

  const handleSaveHint = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (activeSurveyId && surveyHint !== originalHint) {
      const currentSurvey = await fetchSurvey(activeSurveyId);
      const updateData = {
        ...currentSurvey?.data,
        additionalProperties: {
          description: surveyDescription,
          hint: surveyHint,
          hintForInvekos: surveyHintForInvekos,
          consents: surveyConsents,
        },
      };

      await editSurvey(activeSurveyId, updateData);
      setIsEditingHint(false);
    }
  };

  const handleSaveConsents = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (activeSurveyId && surveyConsents !== originalConsents) {
      const currentSurvey = await fetchSurvey(activeSurveyId);
      const updateData = {
        ...currentSurvey?.data,
        additionalProperties: {
          description: surveyDescription,
          hint: surveyHint,
          hintForInvekos: surveyHintForInvekos,
          consents: surveyConsents,
        },
      };

      await editSurvey(activeSurveyId, updateData);
      setIsEditingConsents(false);
    }
  };

  const handleSaveHintForInvekos = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (activeSurveyId && surveyHintForInvekos !== originalHintForInvekos) {
      const currentSurvey = await fetchSurvey(activeSurveyId);
      const updateData = {
        ...currentSurvey?.data,
        additionalProperties: {
          description: surveyDescription,
          hint: surveyHint,
          hintForInvekos: surveyHintForInvekos,
          consents: surveyConsents,
        },
      };

      await editSurvey(activeSurveyId, updateData);
      setIsEditingHintForInvekos(false);
    }
  };

  const handleSaveDates = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (activeSurveyId && dateRange !== originalDateRange) {
      const currentSurvey = await fetchSurvey(activeSurveyId);
      const updateData = {
        ...currentSurvey?.data,
        startDate: dateRange[0],
        endDate: dateRange[1],
        additionalProperties: {
          description: surveyDescription,
          hint: surveyHint,
          hintForInvekos: surveyHintForInvekos,
          consents: surveyConsents,
        },
      };

      await editSurvey(activeSurveyId, updateData);
      setIsEditingDates(false);
    }
  };

  const dateOrTimeChanged = () => {
    return (
      dateRange[0]?.getTime() !== originalDateRange[0]?.getTime() ||
      dateRange[1]?.getTime() !== originalDateRange[1]?.getTime()
    );
  };

  if (!activeSurveyId) {
    return (
      <FormWrapper>
        <CreateSurveyContainer>
          <SectionTitle>
            {t(TranslationKeys.survey_form_create_new_survey)}
          </SectionTitle>
          <CustomAutoExpandingTextarea
            label={t(TranslationKeys.survey_form_survey_name) as string}
            value={surveyName}
            onChange={(e) => setSurveyName(e.target.value)}
          />
          <CustomAutoExpandingTextarea
            label={t(TranslationKeys.survey_form_survey_description) as string}
            value={surveyDescription}
            onChange={(e) => setSurveyDescription(e.target.value)}
          />
          <FormControlLabel
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '24px',
            }}
            control={
              <Switch
                checked={surveyHint.enabled}
                onChange={(e) =>
                  setSurveyHint({
                    ...surveyHint,
                    enabled: e.target.checked,
                    text: e.target.checked ? surveyHint.text : '',
                  })
                }
                sx={{
                  alignSelf: 'flex-start',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              />
            }
            label={t(TranslationKeys.survey_form_add_survey_hint)}
          />
          {surveyHint.enabled && (
            <CustomAutoExpandingTextarea
              label={t(TranslationKeys.survey_form_survey_hint) as string}
              value={surveyHint.text}
              onChange={(e) =>
                setSurveyHint({
                  ...surveyHint,
                  text: e.target.value,
                })
              }
            />
          )}
          <CustomDateRangePicker
            value={dateRange}
            onChange={(range) => {
              setDateRange(range);
            }}
            allowPastDates={false}
            requireTime={true}
          />
          <ContentRow>
            <CustomAutoExpandingTextarea
              label={t(TranslationKeys.survey_form_hint_for_invekos) as string}
              value={surveyHintForInvekos}
              onChange={(e) => setSurveyHintForInvekos(e.target.value)}
            />
          </ContentRow>
          <br />
          <CustomAutoExpandingTextarea
            label={t(TranslationKeys.survey_form_additional_consent)}
            value={surveyConsents.extraConsent}
            onChange={(e) =>
              setSurveyConsents((prev) => ({
                ...prev,
                extraConsent: e.target.value,
              }))
            }
          />
          <CustomAutoExpandingTextarea
            label={t(TranslationKeys.survey_form_main_consent) as string}
            value={surveyConsents.mainConsent}
            onChange={(e) =>
              setSurveyConsents((prev) => ({
                ...prev,
                mainConsent: e.target.value,
              }))
            }
          />
          <ButtonContainer>
            <ActionButton
              variant="cancel"
              buttonText={t(TranslationKeys.survey_form_cancel)}
              onClickAction={onCancel}
            />
            <ActionButton
              variant="accept-blue"
              buttonText={
                loading
                  ? t(TranslationKeys.survey_form_saving)
                  : t(TranslationKeys.survey_form_create_survey)
              }
              onClickAction={handleSaveSurvey}
              disabled={!isFormValid()}
            />
          </ButtonContainer>
        </CreateSurveyContainer>
      </FormWrapper>
    );
  }

  return (
    <FormWrapper>
      <SectionCard>
        <SectionTitle>
          {t(TranslationKeys.survey_form_survey_title)}
        </SectionTitle>
        <ContentRow>
          <CustomAutoExpandingTextarea
            value={surveyName}
            disabled={!isEditingTitle}
            onChange={(e) => setSurveyName(e.target.value)}
          />
          {!isEditingTitle ? (
            <ActionButton
              variant="accept-blue-light"
              buttonText={t(TranslationKeys.survey_form_edit_title)}
              onClickAction={handleEditTitle}
              icon={<EditIcon />}
            />
          ) : (
            <ActionButton
              variant="accept-blue"
              buttonText={t(TranslationKeys.survey_form_save_title)}
              onClickAction={handleSaveTitle}
              icon={<SaveIcon />}
              disabled={surveyName === originalTitle}
            />
          )}
        </ContentRow>
      </SectionCard>

      <SectionCard>
        <SectionTitle>
          {t(TranslationKeys.survey_form_survey_description_section)}
        </SectionTitle>
        <ContentRow>
          <CustomAutoExpandingTextarea
            value={surveyDescription}
            disabled={!isEditingDescription}
            onChange={(e) => setSurveyDescription(e.target.value)}
          />
          {!isEditingDescription ? (
            <ActionButton
              variant="accept-blue-light"
              buttonText={t(TranslationKeys.survey_form_edit_description)}
              onClickAction={handleEditDescription}
              icon={<EditIcon />}
            />
          ) : (
            <ActionButton
              variant="accept-blue"
              buttonText={t(TranslationKeys.survey_form_save_description)}
              onClickAction={handleSaveDescription}
              icon={<SaveIcon />}
              disabled={surveyDescription === originalDescription}
            />
          )}
        </ContentRow>
      </SectionCard>

      <SectionCard>
        <SectionTitle>
          {t(TranslationKeys.survey_form_survey_hint_section)}
        </SectionTitle>
        <ContentRow>
          <FormControlLabel
            control={
              <Switch
                checked={surveyHint.enabled}
                onChange={(e) =>
                  setSurveyHint({
                    ...surveyHint,
                    enabled: e.target.checked,
                    text: e.target.checked ? surveyHint.text : '',
                  })
                }
                disabled={!isEditingHint}
              />
            }
            label={t(TranslationKeys.survey_form_add_survey_hint)}
          />
          {surveyHint.enabled && (
            <CustomAutoExpandingTextarea
              value={surveyHint.text}
              disabled={!isEditingHint}
              onChange={(e) =>
                setSurveyHint({
                  ...surveyHint,
                  text: e.target.value,
                })
              }
            />
          )}
          {!isEditingHint ? (
            <ActionButton
              variant="accept-blue-light"
              buttonText={t(TranslationKeys.survey_form_edit_hint)}
              onClickAction={handleEditHint}
              icon={<EditIcon />}
            />
          ) : (
            <ActionButton
              variant="accept-blue"
              buttonText={t(TranslationKeys.survey_form_save_hint)}
              onClickAction={handleSaveHint}
              icon={<SaveIcon />}
              disabled={
                JSON.stringify(surveyHint) === JSON.stringify(originalHint)
              }
            />
          )}
        </ContentRow>
      </SectionCard>

      <SectionCard>
        <SectionTitle>
          {t(TranslationKeys.survey_form_survey_date_range)}
        </SectionTitle>
        <DateRangeContainer>
          <CustomDateRangePicker
            value={dateRange}
            onChange={(range) => {
              setDateRange(range);
            }}
            allowPastDates={false}
            requireTime={true}
            disabled={!isEditingDates}
          />
          {!isEditingDates ? (
            <ActionButton
              variant="accept-blue-light"
              buttonText={t(TranslationKeys.survey_form_edit_dates)}
              onClickAction={handleEditDates}
              icon={<EditIcon />}
            />
          ) : (
            <ActionButton
              variant="accept-blue"
              buttonText={t(TranslationKeys.survey_form_save_dates)}
              onClickAction={handleSaveDates}
              icon={<SaveIcon />}
              disabled={!dateOrTimeChanged()}
            />
          )}
        </DateRangeContainer>
      </SectionCard>

      <SectionCard>
        <SectionTitle>
          {t(TranslationKeys.survey_form_hint_for_invekos_title)}
        </SectionTitle>
        <ContentRow>
          <CustomAutoExpandingTextarea
            label={t(TranslationKeys.survey_form_hint_for_invekos)}
            value={surveyHintForInvekos}
            disabled={!isEditingHintForInvekos}
            onChange={(e) => setSurveyHintForInvekos(e.target.value)}
          />
          {!isEditingHintForInvekos ? (
            <ActionButton
              variant="accept-blue-light"
              buttonText={t(TranslationKeys.survey_form_edit_hint)}
              onClickAction={(e) => {
                e.preventDefault();
                setIsEditingHintForInvekos(true);
                setOriginalHintForInvekos(surveyHintForInvekos);
              }}
              icon={<EditIcon />}
            />
          ) : (
            <ActionButton
              variant="accept-blue"
              buttonText={t(TranslationKeys.survey_form_save_hint)}
              onClickAction={handleSaveHintForInvekos}
              icon={<SaveIcon />}
              disabled={surveyHintForInvekos === originalHintForInvekos}
            />
          )}
        </ContentRow>
      </SectionCard>

      <SectionCard>
        <SectionTitle>
          {t(TranslationKeys.survey_form_survey_consents_section)}
        </SectionTitle>
        <ContentRow
          style={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            gap: '24px',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              gap: '16px',
            }}
          >
            <CustomAutoExpandingTextarea
              label={t(TranslationKeys.survey_form_additional_consent)}
              value={surveyConsents.extraConsent}
              disabled={!isEditingConsents}
              onChange={(e) =>
                setSurveyConsents((prev) => ({
                  ...prev,
                  extraConsent: e.target.value,
                }))
              }
            />
            <CustomAutoExpandingTextarea
              label={t(TranslationKeys.survey_form_main_consent) as string}
              value={surveyConsents.mainConsent}
              disabled={!isEditingConsents}
              onChange={(e) =>
                setSurveyConsents((prev) => ({
                  ...prev,
                  mainConsent: e.target.value,
                }))
              }
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            {!isEditingConsents ? (
              <ActionButton
                variant="accept-blue-light"
                buttonText={t(TranslationKeys.survey_form_edit_consents)}
                onClickAction={handleEditConsents}
                icon={<EditIcon />}
              />
            ) : (
              <ActionButton
                variant="accept-blue"
                buttonText={t(TranslationKeys.survey_form_save_consents)}
                onClickAction={handleSaveConsents}
                icon={<SaveIcon />}
                disabled={
                  JSON.stringify(surveyConsents) ===
                  JSON.stringify(originalConsents)
                }
              />
            )}
          </div>
        </ContentRow>
      </SectionCard>

      {activeSurveyId && (
        <SectionCard>
          <SurveyQuestions
            surveyId={activeSurveyId}
            defaultQuestion={true}
            onQuestionsUpdate={(hasQuestions, hasUnsaved) => {
              setHasQuestions(hasQuestions);
              setHasUnsavedQuestions(hasUnsaved);
            }}
            deleteSurveyQuestion={deleteSurveyQuestion}
          />
          <ButtonContainer>
            <ActionButton
              variant="ghost"
              buttonText={t(TranslationKeys.survey_form_cancel)}
              onClickAction={onCancel}
            />
            {hasQuestions && (
              <ActionButton
                variant="accept-blue"
                buttonText={t(TranslationKeys.survey_form_save_survey)}
                onClickAction={onSurveyCreated}
                disabled={hasUnsavedQuestions}
              />
            )}
          </ButtonContainer>
        </SectionCard>
      )}
    </FormWrapper>
  );
};

export default SurveyForm;
